import React from "react";
import { Link } from "react-router-dom";
import { Plus, Copy, Check, RotateCcw, Ban, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { displayProductName } from "@/lib/productName";

export default function DashApiKeys() {
  const [keys, setKeys] = React.useState(null);
  const [products, setProducts] = React.useState([]);
  const [showModal, setShowModal] = React.useState(false);
  const [newKey, setNewKey] = React.useState(null);
  const [form, setForm] = React.useState({ product_id: "", name: "" });
  const [copied, setCopied] = React.useState(false);
  const { t } = useLang();

  const load = () => api.get("/api/dashboard/api-keys").then((r) => setKeys(r.data.items));

  React.useEffect(() => {
    load();
    api.get("/api/dashboard/subscriptions").then((r) => {
      const items = r.data.items.filter((s) => s.status === "active");
      setProducts(items.map((s) => ({ id: s.product_id, name: displayProductName(s.product?.name) })));
    });
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.product_id || !form.name) return;
    try {
      const { data } = await api.post("/api/dashboard/api-keys", form);
      setNewKey(data);
      setShowModal(false);
      setForm({ product_id: "", name: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const revoke = async (id) => {
    try {
      await api.post(`/api/dashboard/api-keys/${id}/revoke`);
      toast.success("Key revoked");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const rotate = async (id) => {
    try {
      const { data } = await api.post(`/api/dashboard/api-keys/${id}/rotate`);
      setNewKey({ key: data.key, name: "Rotated key", prefix: data.prefix });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(newKey.key);
    setCopied(true);
    toast.success(t("common.copied"));
    setTimeout(() => setCopied(false), 1200);
  };

  if (!keys) return <div className="h-40 lp-shimmer rounded-2xl" />;

  return (
    <div className="space-y-6" data-testid="dash-keys-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-white/95">{t("dash.keys.title")}</h1>
          <p className="text-white/50 text-sm mt-1">{t("dash.keys.subtitle")}</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          disabled={products.length === 0}
          className="lp-btn-primary"
          data-testid="create-key-btn"
        >
          <Plus className="w-4 h-4" /> {t("dash.keys.create")}
        </button>
      </div>

      {products.length === 0 && (
        <div className="lp-card p-6 text-white/60 text-sm">
          {t("dash.keys.no_sub")} <Link to="/marketplace" className="text-[#d4af37]">{t("dash.keys.browse")}</Link>.
        </div>
      )}

      <div className="lp-card overflow-hidden">
        <table className="lp-table">
          <thead>
            <tr>
              <th>{t("dash.keys.col.name")}</th><th>{t("dash.keys.col.product")}</th><th>{t("dash.keys.col.prefix")}</th><th>{t("dash.keys.col.last_used")}</th><th>{t("dash.keys.col.status")}</th><th className="text-right">{t("dash.keys.col.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 && (
              <tr><td colSpan={6} className="text-center text-white/50 py-10">{t("dash.keys.empty")}</td></tr>
            )}
            {keys.map((k) => (
              <tr key={k.id} data-testid={`key-row-${k.id}`}>
                <td className="text-white/90">{k.name}</td>
                <td className="text-white/70">{displayProductName(k.product_name)}</td>
                <td className="font-mono text-xs text-white/70">{k.key_prefix}••••••</td>
                <td className="text-white/60 text-xs">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : t("dash.keys.never")}</td>
                <td>
                  {k.revoked ? <span className="lp-chip red">{t("dash.keys.status.revoked")}</span> : <span className="lp-chip green">{t("dash.keys.status.active")}</span>}
                </td>
                <td className="text-right">
                  {!k.revoked && (
                    <div className="inline-flex gap-2">
                      <button className="lp-btn-secondary text-xs py-1.5 px-3" onClick={() => rotate(k.id)} data-testid={`rotate-${k.id}`}>
                        <RotateCcw className="w-3.5 h-3.5" /> {t("dash.keys.rotate")}
                      </button>
                      <button className="lp-btn-secondary text-xs py-1.5 px-3 text-red-300 border-red-400/20" onClick={() => revoke(k.id)} data-testid={`revoke-${k.id}`}>
                        <Ban className="w-3.5 h-3.5" /> {t("dash.keys.revoke")}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="lp-card p-6 max-w-md w-full" data-testid="create-key-modal">
            <h3 className="font-display text-xl text-white">{t("dash.keys.modal.title")}</h3>
            <form onSubmit={create} className="mt-4 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("dash.keys.modal.product")}</label>
                <select className="lp-input" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} required data-testid="modal-product-select">
                  <option value="">{t("dash.keys.modal.product_placeholder")}</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("dash.keys.modal.name")}</label>
                <input className="lp-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("dash.keys.modal.name_placeholder")} required data-testid="modal-key-name" />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="lp-btn-secondary flex-1 justify-center">{t("common.cancel")}</button>
                <button type="submit" className="lp-btn-primary flex-1 justify-center" data-testid="modal-create-submit">{t("common.create")}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Show secret once */}
      {newKey && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="lp-card p-6 max-w-lg w-full" data-testid="new-key-modal">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-[#d4af37]" />
              </div>
              <div>
                <div className="font-display text-lg text-white">{t("dash.keys.secret.title")}</div>
                <div className="text-xs text-white/50">{t("dash.keys.secret.body")}</div>
              </div>
            </div>
            <div className="mt-5 lp-terminal">
              <div className="p-4 font-mono text-sm text-[#d4af37] break-all" data-testid="new-key-value">{newKey.key}</div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={copyKey} className="lp-btn-primary flex-1 justify-center" data-testid="new-key-copy">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? t("common.copied") : t("dash.keys.secret.copy")}
              </button>
              <button onClick={() => setNewKey(null)} className="lp-btn-secondary flex-1 justify-center" data-testid="new-key-close">{t("dash.keys.secret.done")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
